import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { fetchToken, fetchUsers, handleChangeUserRole, handleSuspendUser, handleUpdateUserDetails, handleDeleteUser } from '../ApiHandler/usersFunctions';
import { fetchAllDistricts, fetchAllSchools } from '../ApiHandler/schoolFunctions';
import { fetchExistingLabTypes } from '../ApiHandler/labFunctions';
import { exportToUserCSV, exportToUserExcel, exportToPDF, handlePrint } from '../../utils/Utils';
import { handleCreateUser, handleRestoreUser } from '../ApiHandler/authFunctions';
import { toast } from 'react-toastify';
import usePagination from '../../hooks/usePagination';
import ConfirmationModal from '../../utils/ConfirmationModal';
import Select from 'react-select';

const ROLE_META = {
    1: { label:'Admin',            icon:'bx-crown',  bg:'#fff3cd', color:'#856404' },
    2: { label:'State Officer',    icon:'bx-map',    bg:'#d4edda', color:'#155724' },
    3: { label:'District Officer', icon:'bx-map-pin',bg:'#cce5ff', color:'#004085' },
    4: { label:'Teacher',          icon:'bx-user',   bg:'#e2e3e5', color:'#383d41' }
};
const RoleBadge = ({ roleId }) => {
    const r = ROLE_META[roleId] || { label:'Unknown', icon:'bx-help-circle', bg:'#f8d7da', color:'#721c24' };
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:5,
            padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700,
            background:r.bg, color:r.color }}>
            <i className={`bx ${r.icon}`} />{r.label}
        </span>
    );
};

const inp = {
    width:'100%', padding:'9px 12px', border:'1px solid #dee2e6',
    borderRadius:6, fontSize:13, outline:'none', boxSizing:'border-box', marginBottom:2
};

const UserAccess = () => {
    const [users, setUsers] = useState([]);
    const [deleteData, setDeleteData] = useState(false);
    const [confirmHardDelete, setConfirmHardDelete] = useState(false);
    const [isRestorePromptOpen, setIsRestorePromptOpen] = useState(false);
    const [selectedUser,  setSelectedUser]  = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [searchQuery,   setSearchQuery]   = useState('');
    const [isPopupOpen,   setIsPopupOpen]   = useState(false);
    const [editPopupOpen, setEditPopupOpen] = useState(false);
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [modalAction,   setModalAction]   = useState(null);
    const [loading,       setLoading]       = useState(false);
    const [districts,     setDistricts]     = useState([]);
    const [schools,       setSchools]       = useState([]);
    const [labs,          setLabs]          = useState([]);
    const [newUser,       setNewUser]       = useState({
        fname:'', lname:'', email:'', phone:'', userType:'', district_id:'', school_id:'', specific_lab_id:''
    });

    const fetchAllData = () => {
        fetchAllDistricts(setDistricts);
        fetchAllSchools(setSchools);
        fetchExistingLabTypes(setLabs);
    };

    useEffect(() => {
        fetchToken(setCurrentUserId);
        fetchUsers(setUsers);
        fetchAllData();
    }, []);

    const handleInputChange  = e => { const{name,value}=e.target; setNewUser(p=>({...p,[name]:value})); };
    const handleUpdateChange = e => { const{name,value}=e.target; setSelectedUser(p=>({...p,[name]:value})); };

    const districtOptions = districts.map(d => ({ value: d.id, label: d.district_name }));
    const labOptions = labs.map(l => ({ value: l.lab_type_id || l.id, label: l.lab_type_name || l.lab_name }));
    
    const filteredSchoolsAdd = newUser.district_id 
        ? schools.filter(s => String(s.district_id) === String(newUser.district_id))
        : schools;
    const schoolOptionsAdd = filteredSchoolsAdd.map(s => ({ value: s.id, label: s.school_name }));

    const filteredSchoolsEdit = selectedUser && selectedUser.district_id 
        ? schools.filter(s => String(s.district_id) === String(selectedUser.district_id))
        : schools;
    const schoolOptionsEdit = filteredSchoolsEdit.map(s => ({ value: s.id, label: s.school_name }));

    const confirmHandleDeleteUser = (userId, userEmail) => {
        setDeleteData(false);
        setConfirmHardDelete(false);
        setModalAction({userId, userEmail});
        setIsModalOpen(true);
    };
    const handleConfirmAction = () => { 
        if (deleteData && !confirmHardDelete) {
            toast.error("You must explicitly confirm permanent deletion by checking the box.");
            return;
        }
        setIsModalOpen(false); 
        if (modalAction) handleDeleteUser(modalAction.userId, modalAction.userEmail, currentUserId, setUsers, users, deleteData); 
    };

    const handleEditUserDetails = (user) => {
        fetchAllData();
        setSelectedUser({
            ...user,
            fname: user.fname || '',
            lname: user.lname || '',
            district_id: user.district_id || '',
            school_id: user.school_id || ''
        });
        setEditPopupOpen(true);
    };

    const filteredUsers = users.filter(u =>
        (u.fname + ' ' + u.lname).toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const {
        currentPage, entriesPerPage, currentEntries,
        handlePageChange, handleEntriesChange,
        totalEntries, startEntry, endEntry, totalPages
    } = usePagination(filteredUsers, 10);

    return (
        <div className="artifacts-container">
            <ToastContainer />
            {isModalOpen && (
                <div className="popup-overlay" style={{ zIndex: 9999 }}>
                    <div className="add-user-popup" style={{ width: '90%', maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={{ color: '#d32f2f' }}>Remove Member</h2>
                        <p style={{ fontSize: '14px', marginBottom: '20px' }}>How do you want to remove this member?</p>
                        
                        <div style={{ display:'flex', flexDirection:'column', gap:'15px', marginBottom:'20px' }}>
                            <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', cursor:'pointer' }}>
                                <input type="radio" name="deleteType" checked={!deleteData} onChange={() => setDeleteData(false)} style={{ marginTop:'4px' }} />
                                <div>
                                    <strong style={{ display:'block', fontSize:'15px' }}>Remove member only</strong>
                                    <span style={{ fontSize:'12px', color:'#555' }}>Keep all schools, sessions, labs and historical data safely available in the system.</span>
                                </div>
                            </label>
                            <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', cursor:'pointer' }}>
                                <input type="radio" name="deleteType" checked={deleteData} onChange={() => setDeleteData(true)} style={{ marginTop:'4px' }} />
                                <div>
                                    <strong style={{ display:'block', fontSize:'15px', color:'#d32f2f' }}>Remove member and delete their data</strong>
                                    <span style={{ fontSize:'12px', color:'#555' }}>Permanently delete this user and explicitly delete their associated data. This action may not be reversible.</span>
                                </div>
                            </label>
                        </div>

                        {deleteData && (
                            <div style={{ background: '#ffebee', padding: '10px', borderRadius: '5px', marginBottom: '20px', border: '1px solid #ffcdd2' }}>
                                <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
                                    <input type="checkbox" checked={confirmHardDelete} onChange={(e) => setConfirmHardDelete(e.target.checked)} />
                                    <strong style={{ fontSize:'13px', color:'#b71c1c' }}>Are you absolutely sure? I understand this will permanently delete their data.</strong>
                                </label>
                            </div>
                        )}

                        <div className="popup-actions" style={{ justifyContent: 'flex-end', marginTop: 0 }}>
                            <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            <button type="button" className="save-btn" style={{ background: '#d32f2f' }} onClick={handleConfirmAction}>Continue</button>
                        </div>
                    </div>
                </div>
            )}
            
            {isRestorePromptOpen && (
                <div className="popup-overlay" style={{ zIndex: 9999 }}>
                    <div className="add-user-popup" style={{ width: '90%', maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2>Previous Member Found</h2>
                        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                            The email <strong>{newUser.email}</strong> belongs to a previously removed member. 
                            Their historical schools, sessions, labs and other eligible data are still preserved.
                        </p>
                        <p style={{ fontSize: '14px', marginBottom: '20px' }}>Would you like to restore the previous member account?</p>
                        
                        <div className="popup-actions" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="cancel-btn" onClick={() => setIsRestorePromptOpen(false)}>Cancel</button>
                            <button type="button" className="save-btn" disabled={loading} onClick={async () => {
                                setLoading(true);
                                const res = await handleRestoreUser(newUser);
                                setLoading(false);
                                if (res.success) {
                                    toast.success(res.message);
                                    setIsRestorePromptOpen(false);
                                    setNewUser({ fname:'', lname:'', email:'', phone:'', userType:'', district_id:'', school_id:'', specific_lab_id:'' });
                                    fetchUsers(setUsers);
                                } else {
                                    toast.error(res.message);
                                }
                            }}>
                                {loading ? 'Restoring...' : 'Restore Member'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <header className="artifacts-header"
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <h1>User Access</h1>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {loading && <Oval height="20" width="20" color="blue" ariaLabel="loading" />}
                    <button type="button"
                        style={{ backgroundColor:'#007BFF', padding:'8px 16px', color:'white',
                            border:'none', borderRadius:4, cursor:'pointer', fontWeight:600 }}
                        onClick={() => { fetchAllData(); setIsPopupOpen(true); }}>
                        Create User
                    </button>
                </div>
            </header>

            <div className="artifacts-table-container">
                <div className="header-select-entries">
                    <th className="select-entries">Show
                        <select onChange={handleEntriesChange} value={entriesPerPage}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>entries
                    </th>
                    <th>
                        <div className="table-buttons">
                            <button onClick={() => exportToUserCSV(filteredUsers, 'Users.csv')}>CSV</button>
                            <button onClick={() => exportToUserExcel(filteredUsers, 'Users.xlsx')}>Excel</button>
                            <button onClick={() => exportToPDF('.artifacts-table', 'Users.pdf')}>PDF</button>
                            <button onClick={() => handlePrint('.artifacts-table-container')}>Print</button>
                        </div>
                    </th>
                    <th className="user-search">
                        <label>Search</label>
                        <input type="text" placeholder="Type Name or Email..."
                            className="user-search-bar" value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)} />
                    </th>
                </div>

                <div className="artifacts-table-view">
                    <table className="artifacts-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Geography / School</th>
                                <th>Role</th>
                                <th>Delete</th>
                                <th>Change Role</th>
                                <th>Suspend</th>
                                <th>Edit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentEntries.map((user, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td style={{ fontWeight:600 }}>{user.fname} {user.lname}</td>
                                    <td style={{ fontSize:13, color:'#555', wordBreak: 'break-word', maxWidth: '160px' }}>{user.email}</td>

                                    <td>
                                        <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-start' }}>
                                            {user.district_id && (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:4,
                                                    background:'#e3f2fd', color:'#1565c0', border:'1px solid #bbdefb',
                                                    borderRadius:12, padding:'3px 10px', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>
                                                    <i className="bx bx-map-pin" style={{ fontSize:12 }} />
                                                    District ID: {user.district_id}
                                                </span>
                                            )}
                                            {user.school_id && (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:4,
                                                    background:'#e8f5e9', color:'#2e7d32', border:'1px solid #c8e6c9',
                                                    borderRadius:12, padding:'3px 10px', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>
                                                    <i className="bx bxs-school" style={{ fontSize:12 }} />
                                                    School ID: {user.school_id}
                                                </span>
                                            )}
                                            {user.specific_lab_id && (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:4,
                                                    background:'#fce4ec', color:'#c2185b', border:'1px solid #f8bbd0',
                                                    borderRadius:12, padding:'3px 10px', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>
                                                    <i className="bx bx-test-tube" style={{ fontSize:12 }} />
                                                    Lab: {user.specific_lab_name || user.specific_lab_id}
                                                </span>
                                            )}
                                            {!user.district_id && !user.school_id && !user.specific_lab_id && (
                                                <span style={{ color:'#bbb', fontSize:12 }}>—</span>
                                            )}
                                        </div>
                                    </td>

                                    <td><RoleBadge roleId={user.role_id} /></td>

                                    <td>
                                        <button className="delete-btn"
                                            onClick={() => confirmHandleDeleteUser(user.id, user.email)}
                                            disabled={user.id === currentUserId}
                                            style={{ opacity:user.id===currentUserId ? .5 : 1, cursor:user.id===currentUserId?'not-allowed':'pointer' }}>
                                            <i className="bx bx-trash" /> Delete
                                        </button>
                                    </td>

                                    <td>
                                        <select className="role-select-dropdown" value={user.role_id}
                                            onChange={e => handleChangeUserRole(user.id, user.email,
                                                parseInt(e.target.value), currentUserId, setUsers, users)}>
                                            <option value={1}>Admin</option>
                                            <option value={2}>State Officer</option>
                                            <option value={3}>District Officer</option>
                                            <option value={4}>Teacher</option>
                                        </select>
                                    </td>

                                    <td>
                                        <label className="switch">
                                            <div className="in-row-input">
                                                <input type="checkbox"
                                                    checked={user.status === 'inactive'}
                                                    disabled={loading}
                                                    onChange={e => handleSuspendUser(e, user.id,
                                                        currentUserId, user.username, user.email,
                                                        user.status, setUsers, setLoading)} />
                                                <span className="slider round" />
                                            </div>
                                        </label>
                                    </td>

                                    <td>
                                        <a href="# " className="edit-link" onClick={() => handleEditUserDetails(user)}>
                                            ✏️ Edit
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="pagination">
                    <p>Showing {startEntry} to {endEntry} of {totalEntries} entries</p>
                    <div className="pagination-buttons">
                        <button onClick={() => handlePageChange(currentPage-1)} disabled={currentPage===1}>Previous</button>
                        {Array.from({ length:totalPages }, (_,i) => (
                            <button key={i+1} className={currentPage===i+1?'active':''}
                                onClick={() => handlePageChange(i+1)}>{i+1}</button>
                        ))}
                        <button onClick={() => handlePageChange(currentPage+1)} disabled={currentPage===totalPages}>Next</button>
                    </div>
                </div>
            </div>

            {isPopupOpen && (
                <div className="popup-overlay">
                    <div className="add-user-popup">
                        <h2>Register New User</h2>
                        <form>
                            <div><input type="text"  name="fname"  placeholder="First Name" style={inp} value={newUser.fname}    onChange={handleInputChange} required /></div>
                            <div><input type="text"  name="lname"  placeholder="Last Name"  style={inp} value={newUser.lname}    onChange={handleInputChange} required /></div>
                            <div><input type="email" name="email"  placeholder="Email"      style={inp} value={newUser.email}    onChange={handleInputChange} required /></div>
                            <div><input type="text"  name="phone"  placeholder="Phone" maxLength={10} style={inp} value={newUser.phone} onChange={handleInputChange} required /></div>
                            <div>
                                <select name="userType" style={inp} value={newUser.userType} onChange={handleInputChange} required>
                                    <option value="">Select User Type</option>
                                    <option value="Admin">Admin</option>
                                    <option value="State Officer">State Officer</option>
                                    <option value="District Officer">District Officer</option>
                                    <option value="Teacher">Teacher</option>
                                </select>
                            </div>
                            {/* District Selection is shown for District Officer and Teacher (as a filter) */}
                            {(newUser.userType === 'District Officer' || newUser.userType === 'Teacher') && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={districtOptions}
                                        placeholder="Search & Select District"
                                        value={districtOptions.find(d => String(d.value) === String(newUser.district_id)) || null}
                                        onChange={(selected) => setNewUser(p => ({ ...p, district_id: selected ? selected.value : '', school_id: '' }))}
                                        isClearable
                                        required={newUser.userType === 'District Officer'}
                                    />
                                </div>
                            )}
                            {newUser.userType === 'Teacher' && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={schoolOptionsAdd}
                                        placeholder="Search & Select School (Optional)"
                                        value={schoolOptionsAdd.find(s => String(s.value) === String(newUser.school_id)) || null}
                                        onChange={(selected) => setNewUser(p => ({ ...p, school_id: selected ? selected.value : '' }))}
                                        isClearable
                                    />
                                </div>
                            )}
                            {/* Lab Selection */}
                            {(newUser.userType === 'Teacher' || newUser.userType === 'District Officer' || newUser.userType === 'State Officer') && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={labOptions}
                                        placeholder={newUser.userType === 'Teacher' ? "Search & Select Lab *" : "Search & Select Lab (Optional)"}
                                        value={labOptions.find(l => String(l.value) === String(newUser.specific_lab_id)) || null}
                                        onChange={(selected) => setNewUser(p => ({ ...p, specific_lab_id: selected ? selected.value : '' }))}
                                        isClearable
                                        required={newUser.userType === 'Teacher'}
                                    />
                                </div>
                            )}
                            <div className="popup-actions">
                                <button type="submit" className="save-btn" disabled={loading}
                                    onClick={async (e) => {
        const res = await handleCreateUser(e, newUser, setNewUser, setLoading, setIsPopupOpen);
        if (res && res.status === 'removed_exists') {
            setIsRestorePromptOpen(true);
            setIsPopupOpen(false);
        } else if (res && res.success) {
            setNewUser({ fname:'', lname:'', email:'', phone:'', userType:'', district_id:'', school_id:'', specific_lab_id:'' });
            setIsPopupOpen(false);
            fetchUsers(setUsers);
        }
    }}>
                                    {loading ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" className="cancel-btn" onClick={() => setIsPopupOpen(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {editPopupOpen && selectedUser && (
                <div className="popup-overlay">
                    <div className="add-user-popup">
                        <h2>Edit User Details</h2>
                        <form>
                            <div><label style={{ fontSize:13, color:'#666', display:'block', marginBottom:8 }}>📧 {selectedUser.email}</label></div>
                            <div><input type="text" name="fname" placeholder="First Name" style={inp} value={selectedUser.fname} onChange={handleUpdateChange} required /></div>
                            <div><input type="text" name="lname" placeholder="Last Name"  style={inp} value={selectedUser.lname} onChange={handleUpdateChange} required /></div>
                            <div><input type="text" name="phone" placeholder="Phone" maxLength={10} style={inp} value={selectedUser.phone||''} onChange={handleUpdateChange} required /></div>
                            
                            {(selectedUser.role_id === 3 || selectedUser.role_id === 4) && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={districtOptions}
                                        placeholder="Search & Select District"
                                        value={districtOptions.find(d => String(d.value) === String(selectedUser.district_id)) || null}
                                        onChange={(selected) => setSelectedUser(p => ({ ...p, district_id: selected ? selected.value : '', school_id: '' }))}
                                        isClearable
                                    />
                                </div>
                            )}
                            {selectedUser.role_id === 4 && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={schoolOptionsEdit}
                                        placeholder="Search & Select School (Optional)"
                                        value={schoolOptionsEdit.find(s => String(s.value) === String(selectedUser.school_id)) || null}
                                        onChange={(selected) => setSelectedUser(p => ({ ...p, school_id: selected ? selected.value : '' }))}
                                        isClearable
                                    />
                                </div>
                            )}
                            {/* Lab Selection */}
                            {(selectedUser.role_id === 2 || selectedUser.role_id === 3 || selectedUser.role_id === 4) && (
                                <div style={{ marginBottom: 10 }}>
                                    <Select 
                                        options={labOptions}
                                        placeholder={selectedUser.role_id === 4 ? "Search & Select Lab *" : "Search & Select Lab (Optional)"}
                                        value={labOptions.find(l => String(l.value) === String(selectedUser.specific_lab_id)) || null}
                                        onChange={(selected) => setSelectedUser(p => ({ ...p, specific_lab_id: selected ? selected.value : '' }))}
                                        isClearable
                                        required={selectedUser.role_id === 4}
                                    />
                                </div>
                            )}
                            <div className="popup-actions">
                                <button type="submit" className="save-btn" disabled={loading}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        const success = await handleUpdateUserDetails(e, selectedUser, setLoading, setEditPopupOpen);
                                        if (success) fetchUsers(setUsers);
                                    }}>
                                    {loading ? 'Updating...' : 'Update'}
                                </button>
                                <button type="button" className="cancel-btn" onClick={() => setEditPopupOpen(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccess;
